import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { authFetch } from '../utils/authFetch';
import {
  Container, Stack, Card, Group, Text, Button, Textarea,
  Badge, Divider, Alert, Stepper,
  ThemeIcon, Switch, ActionIcon, Tooltip, RingProgress,
  Accordion, Paper, SimpleGrid, ScrollArea, Center
} from '@mantine/core';
import {
  IconCompass, IconCheck, IconAlertCircle,
  IconFileText, IconArrowLeft, IconShieldCheck, IconChartBar
} from '@tabler/icons-react';

import NavigatorNarrativeCard from './components/NavigatorNarrativeCard';
import NavigatorRiskAutopilot from './NavigatorRiskAutopilot';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
import DocumentUpload from '../components/DocumentUpload';
import StepContent from '../components/StepContent';
import { fetchContextData } from '../redux/actions/contextActions';
import {
  selectAssessmentById,
  updateAssessmentStatusByUuid,
  setAssessmentPosition,
  updateAssessmentContext,
  initializeAssessmentLocal
} from '../redux/slices/assessmentsSlice';
import {
  setCurrentAssessment,
  setCurrentModel
} from '../redux/slices/appSlice';
import {
  selectNavigatorRisks,
  selectSavedNavigatorRisks
} from '../redux/slices/risksSlice';

/**
 * NavigatorAssessmentFlow - Main orchestrator for Navigator (use_case) assessments
 * Guides user through: Describe Use Case → Risk Analysis → Review → Report
 * Step 1 collects use case description inline before risk analysis begins
 */
export default function NavigatorAssessmentFlow() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { modelId, assessmentUuid } = useParams();
  const { isLoading: authLoading, isAuthenticated } = useAuth0();

  // Redux state
  const assessment = useSelector(state => selectAssessmentById(state, assessmentUuid));
  const modelName = useSelector(state => state.models?.byId?.[modelId]?.model_name || 'Unknown Model');
  const parentAssessmentUuid = assessment?.parent_assessment_uuid;

  // Template context fields for dynamic rendering
  const templateContextFields = assessment?.templateConfig?.taxonomy?.contextFields?.useCaseProfile;
  // Context data from Redux context slice (populated by StepContent auto-save)
  const contextData = useSelector(state => state.context?.data?.[modelId] || {});
  const useCaseProfileData = useMemo(() => contextData?.useCaseProfile || {}, [contextData?.useCaseProfile]);

  // Get parent Builder assessment for inherited context
  const parentAssessment = useSelector(state =>
    parentAssessmentUuid ? selectAssessmentById(state, parentAssessmentUuid) : null
  );
  const inheritedBucket = parentAssessment?.bucket || assessment?.bucket;
  const inheritedPath = parentAssessment?.path || assessment?.path;

  // Navigator risks from Redux (Navigator-specific state)
  const navigatorRisks = useSelector(state => selectNavigatorRisks(state, assessmentUuid));
  const savedRisks = useSelector(state => selectSavedNavigatorRisks(state, assessmentUuid));

  // Step workflow state
  const [currentStep, setCurrentStep] = useState('describeUseCase');
  const [useCaseDescription, setUseCaseDescription] = useState('');
  const [approvedNarrative, setApprovedNarrative] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const savedAutonomous = assessment?.info?.autonomous;
  const [autonomous, setAutonomous] = useState(savedAutonomous === true || savedAutonomous === 1);
  const [useCaseDocuments, setUseCaseDocuments] = useState([]);
  const [currentAreaProgress, setCurrentAreaProgress] = useState({
    currentArea: null,
    phase: null,
    completedAreas: 0,
    totalAreas: 6
  });

  // Navigator steps
  const steps = [
    { key: 'describeUseCase', label: 'Describe Use Case', icon: IconCompass },
    { key: 'narrative', label: 'Context & Narrative', icon: IconCompass },
    { key: 'risks', label: 'Risk Analysis', icon: IconShieldCheck },
    { key: 'review', label: 'Review & Refine', icon: IconCheck },
    { key: 'report', label: 'Generate Report', icon: IconFileText }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  // Navigator area labels for review display (areas come from template via autopilot)
  const NAVIGATOR_AREA_LABELS = useMemo(() => ({
    userSafety: 'User Safety',
    functionalPerformance: 'Functional Performance',
    fairnessAndEquity: 'Fairness & Equity',
    dataAndPrivacy: 'Data & Privacy',
    reliabilityAndContinuity: 'Reliability & Continuity',
    humanOversightAndControl: 'Human Oversight & Control'
  }), []);

  // Initialize assessment context and load template config
  useEffect(() => {
    const initNavigator = async () => {
      try {
        if (modelId && assessmentUuid) {
          dispatch(setCurrentModel(modelId));
          dispatch(setCurrentAssessment({
            modelId,
            assessmentUuid,
            assessmentType: 'use_case'
          }));

          // Fetch saved context data for StepContent
          dispatch(fetchContextData(modelId, assessmentUuid));

          // Fetch template config if not already loaded
          const templateId = assessment?.info?.template_id;
          if (templateId && !assessment?.templateConfig) {
            try {
              const resp = await authFetch('/.netlify/functions/templates-getTemplate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: templateId })
              });
              if (resp.ok) {
                const template = await resp.json();
                dispatch(initializeAssessmentLocal({
                  assessmentUuid,
                  modelId,
                  templateId,
                  templateConfig: template,
                }));
              }
            } catch (templateErr) {
              console.warn('[NavigatorFlow] Failed to fetch template config:', templateErr);
            }
          }

          // Restore use case description if previously saved
          const savedDescription = assessment?.context?.useCaseProfile?.useCaseDescription;
          if (savedDescription) {
            setUseCaseDescription(savedDescription);
          }

          // Resume at correct step based on saved position
          const savedStep = assessment?.position?.majorStep;
          if (savedStep && savedStep !== 'describeUseCase') {
            setCurrentStep(savedStep);
          }

          setError(null);
          setIsLoading(false);
        } else {
          setError('Missing model ID or assessment UUID');
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[NavigatorAssessmentFlow] Initialization error:', err);
        setError(`Failed to initialize Navigator assessment: ${err.message}`);
        setIsLoading(false);
      }
    };

    initNavigator();
  }, [dispatch, modelId, assessmentUuid, assessment?.context?.useCaseProfile?.useCaseDescription, assessment?.position?.majorStep, assessment?.info?.template_id, assessment?.templateConfig]);

  // Handle use case description submission
  const handleUseCaseSubmit = useCallback(() => {
    // When using template fields, pull the main description from context slice
    const description = templateContextFields
      ? (useCaseProfileData?.useCaseDescription || '').trim()
      : useCaseDescription.trim();

    if (!description) return;

    // Save all context fields (StepContent auto-saves, but also persist to assessment context)
    const contextPayload = templateContextFields
      ? { ...useCaseProfileData, useCaseDescription: description }
      : { useCaseDescription: description };

    dispatch(updateAssessmentContext({
      assessmentUuid,
      section: 'useCaseProfile',
      data: contextPayload
    }));

    dispatch(setAssessmentPosition({
      modelId,
      assessmentUuid,
      position: { majorStep: 'narrative', stepCompleted: 'describeUseCase' }
    }));

    setCurrentStep('narrative');
  }, [dispatch, modelId, assessmentUuid, useCaseDescription, templateContextFields, useCaseProfileData]);

  // Handle narrative approval
  const handleNarrativeApproved = useCallback((narrative) => {
    console.log('[NavigatorAssessmentFlow] Narrative approved, moving to risk analysis');
    setApprovedNarrative(narrative);
    setCurrentStep('risks');
  }, []);

  // Handle risk analysis completion
  const handleRiskAnalysisComplete = useCallback(async () => {
    console.log('[NavigatorAssessmentFlow] Risk analysis complete');
    try {
      await dispatch(setAssessmentPosition({
        modelId,
        assessmentUuid,
        position: { majorStep: 'review', stepCompleted: 'risks' }
      }));

      await dispatch(updateAssessmentStatusByUuid({
        modelId,
        assessmentUuid,
        status: 'Completed',
        assessmentType: 'use_case'
      })).unwrap();
    } catch (err) {
      console.error('[NavigatorAssessmentFlow] Failed to update status:', err);
    }

    setCurrentStep('review');
  }, [dispatch, modelId, assessmentUuid]);

  const handleBackToInventory = () => navigate('/inventory');

  const handleStepClick = (stepKey) => {
    const clickedIndex = steps.findIndex(s => s.key === stepKey);
    if (clickedIndex <= currentStepIndex) {
      setCurrentStep(stepKey);
    }
  };

  // Loading states
  if (authLoading || !isAuthenticated) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="xl">
          <AIGeneratingIcon isGenerating size={64} color="#fd7e14" />
          <Text size="lg" c="dimmed" fw={500}>Loading assessment...</Text>
        </Stack>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Container size="lg" py="xl">
        <Stack align="center" gap="md">
          <AIGeneratingIcon isGenerating size={48} />
          <Text>Loading Navigator Assessment...</Text>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          <Text fw={500}>Error Loading Assessment</Text>
          <Text size="sm">{error}</Text>
          <Button mt="md" onClick={handleBackToInventory}>Return to Inventory</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Header */}
        <Card withBorder p="md" style={{ backgroundColor: 'rgba(253, 126, 20, 0.05)' }}>
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Group gap="md">
                <ActionIcon variant="subtle" onClick={handleBackToInventory} title="Back to Inventory">
                  <IconArrowLeft size={20} />
                </ActionIcon>
                <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'orange', to: 'yellow' }}>
                  <IconCompass size={20} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Text fw={600} size="lg">Navigator Assessment</Text>
                  <Text size="sm" c="dimmed">{modelName}</Text>
                </Stack>
              </Group>
            </Stack>

            <Group gap="md">
              <Badge color="orange" variant="light" size="lg">
                Use Case Analysis
              </Badge>
              <Tooltip label={autonomous ? 'Autonomous mode' : 'Manual mode'}>
                <Switch
                  checked={autonomous}
                  onChange={(e) => setAutonomous(e.currentTarget.checked)}
                  label="Autonomous"
                  labelPosition="left"
                  size="sm"
                />
              </Tooltip>
            </Group>
          </Group>
        </Card>

        {/* Stepper */}
        <Card withBorder p="md">
          <Stepper
            active={currentStepIndex}
            onStepClick={(idx) => handleStepClick(steps[idx].key)}
            color="orange"
            size="sm"
          >
            {steps.map((step) => (
              <Stepper.Step
                key={step.key}
                label={step.label}
                icon={<step.icon size={18} />}
                completedIcon={<IconCheck size={18} />}
              />
            ))}
          </Stepper>
        </Card>

        {/* Step 1: Describe Use Case */}
        {currentStep === 'describeUseCase' && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group gap="md">
                <ThemeIcon size="lg" variant="light" color="orange">
                  <IconCompass size={20} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Text fw={600}>Describe Your Use Case</Text>
                  <Text size="sm" c="dimmed">
                    Describe how this AI model will be deployed — its purpose, audience, data access, and deployment context
                  </Text>
                </Stack>
              </Group>

              <Divider />

              {/* Dynamic fields from template, or fallback to hardcoded textarea */}
              {templateContextFields && templateContextFields.length > 0 ? (
                <StepContent
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  section="useCaseProfile"
                  sectionConfig={templateContextFields}
                />
              ) : (
                <Textarea
                  label="Use Case Description"
                  description="Describe the main purpose, scenario, target audience, and how the model will be deployed"
                  placeholder="e.g., This model will be used for autonomous tractor navigation in agricultural fields, guiding tractors along optimal paths while avoiding obstacles..."
                  value={useCaseDescription}
                  onChange={(e) => setUseCaseDescription(e.currentTarget.value)}
                  minRows={5}
                  maxRows={12}
                  autosize
                  required
                />
              )}

              <DocumentUpload
                modelId={modelId}
                documentType="use_case"
                bucket="use-case-docs"
                label="Use Case Documentation"
                description="Upload supporting documents (deployment guides, requirements, etc.)"
                existingDocuments={useCaseDocuments}
                onUploadComplete={(doc) => setUseCaseDocuments(prev => [...prev, doc])}
                onDocumentDeleted={(id) => setUseCaseDocuments(prev => prev.filter(d => d.id !== id))}
              />

              <Group justify="flex-end">
                <Button
                  color="orange"
                  onClick={handleUseCaseSubmit}
                  disabled={templateContextFields ? !useCaseProfileData?.useCaseDescription?.trim() : !useCaseDescription.trim()}
                  rightSection={<IconCheck size={16} />}
                >
                  Continue to Analysis
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {/* Step 2: Context & Narrative */}
        {currentStep === 'narrative' && (
          <NavigatorNarrativeCard
            modelId={modelId}
            assessmentUuid={assessmentUuid}
            parentAssessmentUuid={parentAssessmentUuid}
            bucket={inheritedBucket}
            path={inheritedPath}
            businessContext={templateContextFields ? JSON.stringify(useCaseProfileData) : useCaseDescription}
            useCase={templateContextFields ? (useCaseProfileData?.useCaseDescription || useCaseDescription) : useCaseDescription}
            onApproved={handleNarrativeApproved}
          />
        )}

        {/* Step 3: Risk Analysis */}
        {currentStep === 'risks' && (
          <>
            <Card withBorder p="md" style={{ backgroundColor: 'rgba(253, 126, 20, 0.05)' }}>
              <Group justify="space-between" align="center">
                <Group gap="md">
                  <RingProgress
                    sections={[{
                      value: (currentAreaProgress.completedAreas / currentAreaProgress.totalAreas) * 100,
                      color: 'orange'
                    }]}
                    label={
                      <Text ta="center" size="xs" fw={600}>
                        {currentAreaProgress.completedAreas}/{currentAreaProgress.totalAreas}
                      </Text>
                    }
                    size={60}
                    thickness={6}
                  />
                  <Stack gap={2}>
                    <Text fw={600}>Risk Analysis</Text>
                    <Text size="sm" c="dimmed">
                      {currentAreaProgress.currentArea
                        ? `Processing: ${NAVIGATOR_AREA_LABELS[currentAreaProgress.currentArea] || currentAreaProgress.currentArea}`
                        : currentAreaProgress.completedAreas === currentAreaProgress.totalAreas
                          ? 'All areas completed'
                          : 'Starting analysis...'}
                    </Text>
                    {currentAreaProgress.phase && (
                      <Badge size="xs" variant="light" color="blue">{currentAreaProgress.phase}</Badge>
                    )}
                  </Stack>
                </Group>
                <Button
                  variant="light"
                  size="xs"
                  onClick={() => setCurrentStep('narrative')}
                  leftSection={<IconArrowLeft size={14} />}
                >
                  Back to Narrative
                </Button>
              </Group>
            </Card>

            <NavigatorRiskAutopilot
              modelId={modelId}
              assessmentUuid={assessmentUuid}
              parentAssessmentUuid={parentAssessmentUuid}
              businessContext={templateContextFields ? JSON.stringify(useCaseProfileData) : useCaseDescription}
              approvedNarrative={approvedNarrative}
              autonomous={autonomous}
              onAreaProgress={setCurrentAreaProgress}
              onComplete={handleRiskAnalysisComplete}
            />
          </>
        )}

        {/* Step 4: Review */}
        {currentStep === 'review' && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group gap="md" justify="space-between">
                <Group gap="md">
                  <ThemeIcon size="lg" variant="light" color="orange">
                    <IconCheck size={20} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text fw={600}>Review & Refine</Text>
                    <Text size="sm" c="dimmed">
                      Review identified risks across all use case areas
                    </Text>
                  </Stack>
                </Group>
                <Badge size="lg" color="orange" variant="light">
                  {savedRisks.length} Risks Identified
                </Badge>
              </Group>

              <Divider />

              <SimpleGrid cols={3} spacing="md">
                <Paper withBorder p="md" radius="md">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="blue">
                      <IconChartBar size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Areas Analyzed</Text>
                  </Group>
                  <Text size="xl" fw={700} c="blue" mt="xs">
                    {Object.keys(navigatorRisks).filter(k => navigatorRisks[k]?.areaStatus === 'completed').length} / 6
                  </Text>
                </Paper>
                <Paper withBorder p="md" radius="md">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="orange">
                      <IconAlertCircle size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Total Risks</Text>
                  </Group>
                  <Text size="xl" fw={700} c="orange" mt="xs">{savedRisks.length}</Text>
                </Paper>
                <Paper withBorder p="md" radius="md">
                  <Group gap="xs">
                    <ThemeIcon size="sm" variant="light" color="green">
                      <IconShieldCheck size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Risk Categories</Text>
                  </Group>
                  <Text size="xl" fw={700} c="green" mt="xs">
                    {new Set(savedRisks.map(r => r.category)).size}
                  </Text>
                </Paper>
              </SimpleGrid>

              <ScrollArea.Autosize mah={500}>
                <Accordion variant="separated" radius="md">
                  {Object.entries(navigatorRisks).map(([areaKey, areaData]) => {
                    const areaRisks = areaData?.areaRisks || {};
                    const riskCount = Object.values(areaRisks).flat().filter(r => r.status === 'Saved').length;
                    const areaLabel = NAVIGATOR_AREA_LABELS[areaKey] || areaKey;

                    return (
                      <Accordion.Item key={areaKey} value={areaKey}>
                        <Accordion.Control>
                          <Group justify="space-between" pr="md">
                            <Text fw={500}>{areaLabel}</Text>
                            <Badge color={riskCount > 0 ? 'orange' : 'gray'} variant="light" size="sm">
                              {riskCount} risks
                            </Badge>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          {riskCount === 0 ? (
                            <Text size="sm" c="dimmed" fs="italic">No risks identified in this area</Text>
                          ) : (
                            <Stack gap="xs">
                              {Object.entries(areaRisks).map(([category, risks]) =>
                                risks.filter(r => r.status === 'Saved').map(risk => (
                                  <Paper key={risk.riskId} withBorder p="sm" radius="sm">
                                    <Group justify="space-between" mb="xs">
                                      <Text size="sm" fw={500}>{risk.riskName}</Text>
                                      {risk.priority && (
                                        <Badge
                                          size="xs"
                                          color={risk.priority === 'High' ? 'red' : risk.priority === 'Medium' ? 'orange' : 'green'}
                                        >
                                          {risk.priority}
                                        </Badge>
                                      )}
                                    </Group>
                                    <Group gap="xs">
                                      <Badge size="xs" variant="outline" color="gray">{category}</Badge>
                                      {risk.confidence && (
                                        <Badge size="xs" variant="dot" color="blue">
                                          {Math.round(risk.confidence * 100)}% confidence
                                        </Badge>
                                      )}
                                    </Group>
                                    {risk.rationale && (
                                      <Text size="xs" c="dimmed" mt="xs" lineClamp={2}>{risk.rationale}</Text>
                                    )}
                                  </Paper>
                                ))
                              )}
                            </Stack>
                          )}
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </ScrollArea.Autosize>

              {savedRisks.length === 0 && (
                <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                  <Text size="sm">No risks have been identified yet.</Text>
                  <Button size="xs" variant="light" color="yellow" mt="xs" onClick={() => setCurrentStep('risks')}>
                    Return to Risk Analysis
                  </Button>
                </Alert>
              )}

              <Group justify="flex-end">
                <Button variant="light" onClick={() => setCurrentStep('risks')} leftSection={<IconArrowLeft size={16} />}>
                  Back to Risk Analysis
                </Button>
                <Button
                  color="orange"
                  onClick={() => setCurrentStep('report')}
                  rightSection={<IconFileText size={16} />}
                  disabled={savedRisks.length === 0}
                >
                  Generate Report
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {/* Step 5: Report */}
        {currentStep === 'report' && (
          <Card withBorder p="lg">
            <Stack gap="md">
              <Group gap="md">
                <ThemeIcon size="lg" variant="light" color="orange">
                  <IconFileText size={20} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Text fw={600}>Generate Report</Text>
                  <Text size="sm" c="dimmed">Export Navigator assessment as a comprehensive report</Text>
                </Stack>
              </Group>

              <Divider />

              <Alert color="orange" variant="light">
                <Text size="sm">The report will include:</Text>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Use case description and context</li>
                  <li>Inherited technical risks from Builder assessment</li>
                  <li>Scenario-specific risks by area</li>
                  <li>Suggested controls and mitigations</li>
                  <li>Recommendations for this use case</li>
                </ul>
              </Alert>

              <Group justify="flex-end">
                <Button variant="light" onClick={() => setCurrentStep('review')} leftSection={<IconArrowLeft size={16} />}>
                  Back to Review
                </Button>
                <Button
                  color="orange"
                  leftSection={<IconFileText size={16} />}
                  onClick={() => navigate(`/navigator-report/${modelId}/${assessmentUuid}`)}
                >
                  View Report
                </Button>
              </Group>
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}
